import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';

import { DbKey, makeWithDbId, WithDbId } from '../../../../../adl-gen/common/db';
import { Competency, makeInternationalized } from '../../../../../adl-gen/totalinteraction/futurefit/db';
import { makeCompetency } from '../../../../../adl-gen/totalinteraction/futurefit/frontend';
import { LangCode, withI18n, WithI18nProps } from '../../../../../internationalization/i18n-hoc';
import { Select, SelectOption } from '../../../../../ui/widgets/select/select';
import {
  FieldState,
  newIntegerFieldState,
  newNonEmptyTextField
} from '../../../../../ui/widgets/text-input/text-field';
import {
  deleteHandler,
  I18nInput,
  I18nTextInputObserver,
  renderButtons,
  TextInputObserver
} from '../../../common/editor';

const styles = require('../../../common/editor.css');

interface RestrictedFields {}

interface ItemState {
  name: I18nInput;
  fullName: I18nInput;
  definition: I18nInput;
  ordering: FieldState<number>;
}

// type EnvironmentType = "dev" | "test" | "uat" | "prod";
interface CompetencyEditorProps extends WithI18nProps {
  debug?: boolean;
  item?: WithDbId<Competency>;
  onCreate(item: Competency): Promise<void>;
  onUpdate?(item: WithDbId<Competency>): Promise<void>;
  onCancel(): void;
  role: 'client-admin' | 'super';
}

/**
 * Component to maintain Clients.
 */
@observer
export class CompetencyEditor0 extends React.Component<CompetencyEditorProps> {
  @observable fieldState: ItemState = {
    name: makeInternationalized({ en: newNonEmptyTextField() }),
    fullName: makeInternationalized({ en: newNonEmptyTextField() }),
    definition: makeInternationalized({ en: newNonEmptyTextField() }),
    ordering: newIntegerFieldState()
  };

  restrictedFields: RestrictedFields = {};

  noShowFields = {};

  @observable lang: LangCode;

  @observable locked = false;

  constructor(props: CompetencyEditorProps) {
    super(props);
    const { initI18nEmpty: initEmpty, initI18nFromInput: initFromInput } = props;
    const state = this.fieldState;
    this.lang = props.currentLangCode();
    if (props.role !== 'super') {
      // TODO: enable later
      throw Error('Client admin cannot edit Competency: not authorized');
    }
    if (props.item) {
      const { value } = props.item;
      initFromInput(state.name, value.name);
      initFromInput(state.fullName, value.fullName);
      initFromInput(state.definition, value.definition);
      state.ordering.onChange(`${value.ordering}`);
    } else {
      state.name = initEmpty(state.name);
      state.fullName = initEmpty(state.fullName);
      state.definition = initEmpty(state.definition);
      state.ordering.onChange(`0`);
    }
  }

  @action lockUpdates = () => (this.locked = true);

  @action
  changeLang = (opt: SelectOption) => {
    this.lang = opt.value as LangCode;
  };

  LangSelector = observer(() => {
    const { i18nOptions } = this.props;
    const onChange = this.changeLang;
    const options = i18nOptions();
    return (
      <Select
        options={options}
        defaultValue={options.find(option => (option.value as LangCode) === this.lang)}
        onChange={onChange}
      />
    );
  });

  NameObserver = observer(() =>
    I18nTextInputObserver({
      field: this.fieldState.name,
      type: 'text',
      lang: this.lang,
      title: 'Competency Display Name',
      placeholder: `Name of Competency (abbreviated if required)`,
      xsSpan: 4,
      debug: this.props.debug
    })
  );

  FullNameObserver = observer(() =>
    I18nTextInputObserver({
      field: this.fieldState.fullName,
      type: 'text',
      lang: this.lang,
      title: 'Competency Full Name',
      placeholder: `Name of Competency`,
      xsSpan: 4,
      debug: this.props.debug
    })
  );

  DefinitionObserver = observer(() =>
    I18nTextInputObserver({
      field: this.fieldState.definition,
      type: 'textarea',
      lang: this.lang,
      title: 'Competency Definition',
      placeholder: `Define this Competency`,
      xsSpan: 4,
      debug: this.props.debug
    })
  );

  OrderingObserver = observer(() =>
    TextInputObserver({
      field: this.fieldState.ordering,
      type: 'number',
      title: 'Order in lists',
      placeholder: '0...',
      xsSpan: 1,
      debug: this.props.debug
    })
  );

  @computed
  get inputIsValid(): boolean {
    return (
      this.props.isI18nValid(this.fieldState.name) &&
      this.props.isI18nValid(this.fieldState.fullName) &&
      this.props.isI18nValid(this.fieldState.definition) &&
      this.fieldState.ordering.getValid() !== undefined
    );
  }

  onSave = async () => {
    const { item, makeI18nContent, onCreate, onUpdate } = this.props;

    this.lockUpdates();

    /// Build from scratch here to get latest changes
    const ac = makeCompetency({
      name: makeI18nContent(this.fieldState.name),
      fullName: makeI18nContent(this.fieldState.fullName),
      definition: makeI18nContent(this.fieldState.definition),
      ordering: Number(this.fieldState.ordering.value())
    });

    if (item && onUpdate) {
      await onUpdate(
        makeWithDbId({
          id: item.id,
          value: { ...ac }
        })
      );
    } else {
      await onCreate(ac);
    }
  };

  // TODO: At appropriate time add this.props.role === "super" guards and use restrictedFields if required
  Edit = () => (
    <React.Fragment>
      <h4>Competency/Capability Area</h4>
      <div className={styles.row}>
        <this.LangSelector />
      </div>
      <div className={styles.row}>
        <this.NameObserver />
        <this.FullNameObserver />
        <this.OrderingObserver />
      </div>
      <div className={styles.row}>
        <this.DefinitionObserver />
      </div>
    </React.Fragment>
  );

  render() {
    return (
      // {/* TODO aria (or DOM) labels */}
      <div className={styles.page}>
        <div className={styles.body}>
          <div>
            {renderButtons(
              this.onSave,
              undefined,
              this.props.onCancel,
              () => this.inputIsValid && !this.locked,
              undefined,
              () => !this.locked
            )}
          </div>
          <this.Edit />
        </div>
      </div>
    );
  }
}

export interface CompetencyDeleterProps {
  items?: WithDbId<Competency>[];
  onDelete?(ids: DbKey<Competency>[]): Promise<void>;
  onCancel(): void;
}

/** Base component to create/update Competency */
// @observer
export class CompetencyDeleter extends React.Component<CompetencyDeleterProps> {
  onDelete = async () => {
    const { items, onDelete } = this.props;
    if (items && onDelete) {
      await onDelete(items.map(item => item.id));
    }
  };

  render() {
    const { onCancel, items } = this.props;
    return items ? (
      <div className={styles.page}>
        <div className={styles.body}>
          <div className={styles.row}>
            {deleteHandler(this.onDelete, onCancel, 'Competencies', items.map(item => item.value.name.value.en))}
          </div>
        </div>
      </div>
    ) : null;
  }
}

export const CompetencyEditor = withI18n(CompetencyEditor0);



// WEBPACK FOOTER //
// ./src/admin/ui/components/editors/competency-editor/competency-editor.tsx